import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import DropdownMenu from '../ui/DropdownMenu';
import { Button } from '../ui/Button';
import StatusPill, { humanizeEnum } from '../ui/StatusPill';
import { SignaturePill } from '../ui/StatusPill';
import useTableSort from '../ui/useTableSort';
import { SalespeopleChips } from '../ui/SalespeopleChips';
import { normaliseSalespeople, resolveQuoteSalespeople, quoteDateValue, looksLikeRawId } from '../../utils/normaliseSalespeople';
import { useSettingsContext } from '../../hooks/useSettings';
import { useConfirm } from '../ui';
import { formatQuoteNumber } from '../../utils/displayId';
import { formatCurrency } from '../../utils/formatCurrency';
import { getTotalAmount } from '../../utils/canonicalFields';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../ui/Table';
import { Alert } from '../ui/Alert';
import Skeleton from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import ListShell from '../ui/ListShell';
import PageHeader from '../ui/PageHeader';
import Checkbox from '../ui/Checkbox';
import BulkActionBar from '../ui/BulkActionBar';
import { quoteAPI } from '../../services/api';
import { lastSendAttachments, canIncludeInPdf, pdfRefusalMessage } from '../../utils/quotePdfFiles';
import { resolveDisplayStatus } from '../../quote-builder';
import { getQuoteRoute } from '../../utils/quoteRouting';
import { originState } from '../../utils/backOrigin';
import {
  isArchived,
  isConverted,
  isSent,
  isCanonicalActive,
  isCanonicalSent,
} from '../../utils/quoteStatus';
import api from '../../services/api';
import MarkAsLostModal from './MarkAsLostModal';
import { dealerToday } from '../../utils/dealerDate';

// IDI-157: a quote/job opened from this list goes Back to it.
const QUOTES_LIST_ORIGIN = originState({ from: '/quotes', label: 'Back to Quotes' });

// Quick-filter chips (RF-17-1: no KPI cards, chips only — RF-17-7: vocab seeds)
// PT-53A W2.2 (DEF-037 + DEF-117): chip taxonomy revised per operator ruling
// 2026-07-30. Archived is a DIMENSION, not a status. A quote can be SENT and
// ARCHIVED simultaneously. The prior "one primary chip per quote" ruling was
// unsatisfiable against real data.
//
// Revised ruling:
//   Status chips (Active · Sent · Awaiting Signature · Lost · Expired)
//     — mutually exclusive, one per quote, operate on the NON-ARCHIVED population.
//   Archived — a TOGGLE, not a chip. Off by default; when on, it filters the
//     current status selection to archived records.
//   All — means all non-archived, non-converted. Its count MUST equal its row count.
//
// INVARIANT (the assertion that makes the whole thing safe):
//   For every chip, count === renderedRows.length.
//   One function produces both. Not two functions that agree by luck.
//
// Derivation helpers. isArchived/isSent were LOCAL until PR-41; extracted to
// utils/quoteStatus.js so the dashboard and this page compute "open" against
// the same predicate. Do not re-implement here — import from the module.
// isAwaitingSignature stays local: it's a QuotesPage-only chip predicate.
function isAwaitingSignature(q) {
  const sig = q.signatureStatus || q.signature_status;
  return isSent(q) && sig === 'acknowledged_pending';
}

// Status predicates — mutually exclusive on the non-archived population.
//
// IDI-146 (2026-09-08): `.active` and `.sent` now route through the
// canonical predicates in utils/quoteStatus.js (isCanonicalActive,
// isCanonicalSent). The chip was the correct-half source on isSent
// exclusion + draft inclusion; the pipeline was the correct-half source
// on case-insensitive status matching. The canonical form combines both.
//
// Do NOT inline `q.status === 'active'` or similar here — the utility
// is the single source of truth for the bucket decision. A callable
// wrapper preserves the ({ [chipKey]: predicate }) shape that
// filterQuotesForChip.STATUS_PREDICATES[chipKey](q) consumes below.
const STATUS_PREDICATES = {
  active: isCanonicalActive,
  // DEC-39: Awaiting Signature chip removed; those quotes now appear under Sent.
  sent: isCanonicalSent,
  awaiting_signature: isAwaitingSignature,
  lost: (q) => q.status === 'lost',
  expired: (q) => q.status === 'expired',
};

/**
 * The SINGLE filtering function that produces both chip counts AND rendered rows.
 * `chipKey` is the active chip; `showArchived` is the toggle state.
 *
 * DEF-037 + DEF-117 invariant: count === rows.length, by construction.
 */
function filterQuotesForChip(allQuotes, chipKey, showArchived) {
  return allQuotes.filter((q) => {
    // Converted always excluded from listing.
    if (isConverted(q)) return false;
    // Archived dimension: show archived ONLY when the Archived toggle is on.
    if (showArchived) {
      if (!isArchived(q)) return false;
    } else {
      if (isArchived(q)) return false;
    }
    // Status chip filter (All passes everything that survived above).
    if (chipKey && chipKey !== 'all' && chipKey !== 'archived') {
      const pred = STATUS_PREDICATES[chipKey];
      if (pred && !pred(q)) return false;
    }
    return true;
  });
}

const QUICK_FILTER_CHIPS = [
  { key: 'all', label: 'All' },
  // DEC-39 (2026-07-30): chips cut to 5. Awaiting Signature is findable
  // via Sent; Expired is a row state visible under every filter, not a
  // population worth its own chip.
  // Q-B1 (2026-08-18, 347_ §3): Lost chip removed. The per-row status pill
  // ("row TAG") that renders 'Lost' on a lost quote's row still exists;
  // only the quick-filter pill is gone. Lost quotes are findable via the
  // Archived chip because §3al auto-archives on Lost.
  { key: 'active', label: 'Active' },
  { key: 'sent', label: 'Sent' },
  { key: 'archived', label: 'Archived' },
];

// normalizeStatusStatic: used for per-row action gating (the row badge goes
// through resolveDisplayStatus). NOT used for chip filtering.
// IDI-370: won/accepted is no longer folded into 'active' — it is its own
// "Accepted" state. It stays convertible, but (IDI-544) a signed quote never
// offers Mark as Lost.
function normalizeStatusStatic(status) {
  switch (status) {
    case 'won': case 'accepted': return 'won';
    case 'sent': return 'active';
    case 'converted': return 'converted';
    default: return status || 'draft';
  }
}

function SkeletonRow() {
  return (
    <TableRow>
      {[...Array(10)].map((_, i) => (
        <TableCell key={i}>
          <Skeleton w={`${60 + Math.random() * 40}%`} h="1em" />
        </TableCell>
      ))}
    </TableRow>
  );
}

export default function QuotesPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [users, setUsers] = useState([]);
  const { getEnumLabel } = useSettingsContext();

  // IDI-164: Job Type cell. Server label (resolved across the legacy JOBTYPE#
  // and settings id spaces) first; the client settings cache only as a
  // fallback, and only when it returns a real label rather than echoing /
  // prettifying the raw id back. Never renders a raw id.
  const jobTypeLabelFor = (quote) => {
    const server = typeof quote.jobTypeLabel === 'string' ? quote.jobTypeLabel.trim() : '';
    if (server) return server;
    const raw = quote.jobTypeId == null ? '' : String(quote.jobTypeId).trim();
    if (!raw || looksLikeRawId(raw)) return '—';
    const label = getEnumLabel ? getEnumLabel('job_types', raw) : '';
    if (!label) return '—';
    if (String(label).trim() === raw) return '—';
    return label;
  };
  const [loading, setLoading] = useState(true);
  // IDI-56: true while more quote batches are still streaming in behind an
  // already-rendered first page. Purely informational (small inline note);
  // never blocks interaction.
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  // IDI-56: server-computed chip counts (GET /v1/quotes?action=counts),
  // fetched once, independent of the paginated row fetch. Used ONLY while
  // backgroundLoading is true, so the chips show correct totals during the
  // window where `quotes` doesn't have everything yet. Once loading settles,
  // filterQuotesForChip(quotes, ...) is authoritative again and the
  // DEF-037/DEF-117 invariant (count === rendered rows.length) holds exactly
  // as before -- this only fills the gap mid-load, it never overrides the
  // settled count.
  const [serverCounts, setServerCounts] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');

  // Quick-filter chip state
  const [activeChip, setActiveChip] = useState(() => {
    const chipParam = searchParams.get('chip');
    if (chipParam) return chipParam;
    // PT-15 #10 (2026-07-24): default to 'all' (not null) so the All chip is
    // visibly highlighted on page load. Filter (below) already treats 'all'
    // and null identically (show everything), so behavior is unchanged —
    // Operator ruling (2026-08-13): default to 'active', not 'all'.
    return 'active';
  });
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // PR-64 §1.1 (R-77): multi-select for bulk actions on the quotes list.
  // ARRAY shape (not Set) to mirror CustomersPage's reference implementation.
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkError, setBulkError] = useState(null);
  const [bulkSuccess, setBulkSuccess] = useState(null);

  // Write state to URL
  const syncToURL = useCallback((chip, search) => {
    const params = new URLSearchParams();
    if (chip) params.set('chip', chip);
    if (search) params.set('search', search);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncToURL(activeChip, searchTerm);
  }, [activeChip, searchTerm, syncToURL]);

  useEffect(() => {
    loadQuotes();
    loadUsers();
  }, []);

  // PT-53A W2.2 (DEF-104): refetch on window focus / bfcache restore.
  // React Router v6 unmounts/remounts sibling routes on navigation, so
  // the mount-effect SHOULD fire on back-nav. But modern browsers' bfcache
  // restores the entire DOM without remounting React — the user sees stale
  // data until a hard reload. This listener catches both `visibilitychange`
  // (tab switch) and `pageshow` (bfcache restore) to ensure the landing
  // table always reflects the latest state after the user returns from an
  // edit or detail page.
  useEffect(() => {
    const refetchOnFocus = () => { if (!document.hidden) loadQuotes(); };
    const refetchOnPageShow = (e) => { if (e.persisted) loadQuotes(); };
    document.addEventListener('visibilitychange', refetchOnFocus);
    window.addEventListener('pageshow', refetchOnPageShow);
    return () => {
      document.removeEventListener('visibilitychange', refetchOnFocus);
      window.removeEventListener('pageshow', refetchOnPageShow);
    };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // IDI-56: PROGRESSIVE loading. The backend now supports opt-in cursor
  // pagination (limit/cursor -> {quotes, has_more, next_cursor}); a tenant
  // with thousands of quotes (2,456 rows / ~1MB on t270) used to block the
  // whole page behind one multi-second fetch of the ENTIRE table before
  // rendering anything.
  //
  // Fetch the first batch, render immediately, then keep fetching more
  // batches in the background and MERGE them into the same `quotes` state
  // that filterQuotesForChip already reads for both counts and rows. This
  // is deliberately NOT "true" server-side browsing pagination — chip
  // counts, search, and sort all still operate client-side over whatever
  // has loaded so far, so the DEF-037/DEF-117 invariant (count ===
  // rows.length) is preserved BY CONSTRUCTION, exactly as before: nothing
  // about filterQuotesForChip changed. The only difference is `quotes`
  // grows in batches instead of arriving as one blocking response.
  //
  // `loadSeq` guards against a stale background fetch (from an earlier
  // loadQuotes() call, e.g. the focus/pageshow refetch firing mid-load)
  // continuing to append to `quotes` after a newer load has started.
  const loadSeqRef = React.useRef(0);
  const QUOTES_BATCH_SIZE = 500;

  const loadQuotes = async () => {
    const mySeq = ++loadSeqRef.current;
    let first = true; // declared outside try/catch so the catch block can see it
    try {
      setLoading(true);
      setBackgroundLoading(false);
      setQuotes([]);
      setServerCounts(null);

      // Fire-and-forget: fills the chip-count gap while batches are still
      // streaming in. Never blocks the row fetch and a failure here is
      // silent -- filterQuotesForChip(quotes, ...) is always the fallback.
      // Guarded with typeof: some test mocks / older bundles may not define
      // getQuoteCounts at all, and that must never take down the row fetch.
      if (typeof quoteAPI.getQuoteCounts === 'function') {
        Promise.resolve(quoteAPI.getQuoteCounts())
          .then((res) => {
            if (loadSeqRef.current !== mySeq) return;
            const counts = res?.data || res;
            if (counts && typeof counts === 'object') setServerCounts(counts);
          })
          .catch(() => { /* chip counts just stay client-side-derived */ });
      }

      let cursor;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (loadSeqRef.current !== mySeq) return; // superseded by a newer load
        const response = await quoteAPI.getQuotes({ limit: QUOTES_BATCH_SIZE, cursor });
        const body = response.data || response || {};
        const batch = Array.isArray(body) ? body : (body.quotes || []);
        if (loadSeqRef.current !== mySeq) return; // superseded while awaiting

        const isFirstBatch = first;
        setQuotes((prev) => (isFirstBatch ? batch : [...prev, ...batch]));
        if (isFirstBatch) {
          setLoading(false);
          first = false;
        }

        const hasMore = Array.isArray(body) ? false : !!body.has_more;
        cursor = Array.isArray(body) ? undefined : body.next_cursor;
        if (!hasMore || !cursor) break;
        setBackgroundLoading(true);
      }
    } catch (err) {
      if (loadSeqRef.current !== mySeq) return;
      if (first) {
        // Failed before anything rendered — same all-or-nothing error state
        // as the pre-pagination single-fetch behaviour.
        setError('Unable to load quotes.');
        setQuotes([]);
      } else {
        // A LATER batch failed after the page already has good, rendered
        // data. Keep it visible and surface a non-blocking warning instead
        // of discarding rows the operator can already see and use — the
        // list is just incomplete, not broken.
        setError('Some quotes may be missing — retry to load the rest.');
      }
      setLoading(false);
    } finally {
      if (loadSeqRef.current === mySeq) {
        setLoading(false);
        setBackgroundLoading(false);
      }
    }
  };

  const loadUsers = async () => {
    try {
      const r = await api.getUsers();
      setUsers(Array.isArray(r) ? r : r?.data || r?.users || []);
    } catch (err) {
      // PT-33.6 (2026-07-27): surface the failure via the page-level
      // setError so the operator sees why user-scoped filters ("my quotes",
      // salesperson chips) are missing options. Non-fatal — the page keeps
      // rendering with an empty users list — but no longer silent.
      setError('Failed to load users. Some filters may be unavailable.');
    }
  };

  // PT-14 G4 (Wave 3, 2026-07-24): reactivate an expired quote directly
  // from the list kebab (previously only available on the detail page).
  const handleReactivateQuote = async (id) => {
    try {
      await quoteAPI.activateQuote(id);
      await loadQuotes();
    } catch (err) {
      setError('Failed to reactivate quote.');
    }
  };

  const handleArchive = async (id) => {
    if (!(await confirm({ title: 'Archive quote?', message: 'Archive this quote? It will be hidden from the active list.', confirmLabel: 'Archive' }))) return;
    try {
      await quoteAPI.archiveQuote(id);
      setQuotes(quotes.filter(q => q.id !== id));
    } catch (err) {
      // R-80 (2026-08-20): the backend now accepts archive on drafts (main.go archiveQuote handler).
      // Any residual error copy comes from the server. Legacy example copy
      // "Draft quotes should be deleted, not archived" stripped — no longer describes
      // observable behaviour.
      const msg = err?.response?.data?.message || err?.data?.message || 'Failed to archive quote.';
      setError(msg);
    }
  };

  // PR-65 R-80: handleDelete + quoteAPI.deleteQuote left in place — the row-⋮ entry
  // that called this is removed (see actions array below). This function has zero
  // remaining callers as of this commit. FAMILY B candidate for a dead-code sweep
  // in a future PR; deleteQuote endpoint on the backend also becomes orphaned.
  // Not removed here to keep the PR narrowly focused; see PR-65 Nevado report.
  // eslint-disable-next-line no-unused-vars
  const handleDelete = async (id) => {
    if (!(await confirm({
      title: 'Delete quote?',
      message: 'Permanently delete this draft quote? This cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'danger',
    }))) return;
    try {
      await quoteAPI.deleteQuote(id);
      setQuotes(quotes.filter(q => q.id !== id));
    } catch (err) {
      const msg = err?.response?.data?.message || err?.data?.message || 'Failed to delete quote.';
      setError(msg);
    }
  };

  // PT-53A W2.2 (DEF-036): Unarchive from the landing table.
  const handleUnarchive = async (id) => {
    if (!(await confirm({ title: 'Unarchive quote?', message: 'Unarchive this quote? It will return to its previous state.', confirmLabel: 'Unarchive' }))) return;
    try {
      await quoteAPI.unarchiveQuote(id);
      await loadQuotes();
    } catch (err) { setError('Failed to unarchive quote.'); }
  };

  const handleMarkAsLost = async (id) => {
    // §1.1: delegate to the MarkAsLostModal (opened via state) rather than the
    // bare confirm() dialog. See below for the modal wiring + submitMarkLost.
    openMarkLost(id);
  };

  // §3 (2026-07-29) D-10: Download PDF row action. Generates + opens the PDF.
  // The backend returns a URL (S3 signed) OR a base64 payload depending on impl.
  // Handle both by preferring `url` and falling back to a blob.
  const handleDownloadPDF = async (id) => {
    try {
      // IDI-487 (D6): the latest send's ticked PDFs and images follow the
      // quote, else none. List rows don't carry sendComposer; read the quote.
      let files = [];
      try {
        const full = await quoteAPI.getQuote(id);
        files = lastSendAttachments(full?.data || full).filter(canIncludeInPdf);
      } catch { /* none */ }
      const res = await quoteAPI.generatePDF(id, null, files);
      const url = res?.url || res?.data?.url || res?.pdf_url;
      if (url) {
        window.open(url, '_blank', 'noopener');
      } else if (res?.pdf_base64 || res?.data?.pdf_base64) {
        const b64 = res.pdf_base64 || res.data.pdf_base64;
        const blob = new Blob([Uint8Array.from(atob(b64), c => c.charCodeAt(0))], { type: 'application/pdf' });
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, '_blank', 'noopener');
      } else {
        setError('PDF generated but no URL returned.');
      }
    } catch (err) {
      setError(pdfRefusalMessage(err) || 'Failed to generate PDF.');
    }
  };

  // §1.1 (2026-07-29): Mark-as-Lost modal (D-10). Prior confirm() dialog shipped
  // the container without its contents — no loss reason capture, no "Also archive"
  // checkbox. `MarkAsLostModal` renders both. `quoteAPI.markAsLost(id, reason)`
  // already accepts a reason (services/api.js:141); it was never being sent.
  const [markLostQuoteId, setMarkLostQuoteId] = useState(null);
  const openMarkLost = (id) => setMarkLostQuoteId(id);
  const closeMarkLost = () => setMarkLostQuoteId(null);
  // §3al (2026-08-18, 348_ Q6): Lost ALWAYS archives. And per PR-51's ruling,
  // if the archive fails, that is an ERROR — not a silent success. The
  // previous catch-swallow ('} catch { /* non-fatal */ }') hid archive
  // failures completely; a user who saw 'Lost' render would have believed
  // the quote was also archived when in fact it might not have been.
  //
  // Optimistic archivedAt stamping is also gone: we only mark the row
  // archived in local state AFTER the server confirmed the archive.
  const submitMarkLost = async ({ reason, alsoArchive: _ignoredButPreservedForCompat }) => {
    const id = markLostQuoteId;
    if (!id) return;
    try {
      await quoteAPI.markAsLost(id, reason);
      // Auto-archive per §3al. Do NOT catch this separately — let a failure
      // propagate to the outer catch so the user learns the archive did not
      // complete, rather than seeing a Lost row without an archivedAt stamp.
      await quoteAPI.archiveQuote(id);
      // Both calls succeeded: stamp status='lost' AND archivedAt in local state.
      // We use the server's timestamp shape (ISO 8601) so downstream
      // comparators (isArchivedRow) see the same string a fresh fetch would.
      setQuotes(quotes.map(q => q.id === id
        ? { ...q, status: 'lost', archivedAt: new Date().toISOString() }
        : q));
    } catch (err) {
      // Un-swallowed per §3al. The specific message could be improved once
      // parseApiError tells us WHICH call failed; for now this is honest.
      // eslint-disable-next-line no-console
      console.error('[QuotesPage] Mark Lost failed:', err);
      setError('Failed to mark quote as lost. Please try again.');
    } finally {
      closeMarkLost();
    }
  };

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // PT-53A W2.2 (DEF-037 + DEF-117): filteredQuotes uses the SAME
  // `filterQuotesForChip` function that produces chip counts. One function,
  // used twice. The invariant (count === rows.length) holds by construction.
  const showArchived = activeChip === 'archived';
  const filteredQuotes = useMemo(() => {
    if (!Array.isArray(quotes)) return [];
    let filtered = filterQuotesForChip(quotes, activeChip, showArchived);
    // Search filter (applied on top of chip filter).
    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      filtered = filtered.filter((q) => (
        (q.quoteNumber || q.quote_number || q.id || '').toLowerCase().includes(term) ||
        (q.customerName || q.customer_name || '').toLowerCase().includes(term) ||
        (q.title || q.quoteTitle || q.quote_title || '').toLowerCase().includes(term)
      ));
    }
    // PT-09: Default sort — newest first.
    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt || a.created_at || 0);
      const dateB = new Date(b.createdAt || b.created_at || 0);
      return dateB - dateA;
    });
  }, [quotes, debouncedSearch, activeChip, showArchived]);

  // IDI-479: header click-to-sort over ALL loaded rows (after the chip/search
  // filter and PT-09 default order, before pagination). "Off" = PT-09 order.
  const quoteSortColumns = useMemo(() => [
    { key: 'project', type: 'text', value: (q) => q.title || q.quoteTitle || q.quote_title || q.projectName || q.project_name || formatQuoteNumber(q) },
    { key: 'customer', type: 'text', value: (q) => q.customerName || q.customer_name || null },
    { key: 'jobType', type: 'text', value: (q) => jobTypeLabelFor(q) },
    { key: 'salesperson', type: 'text', value: (q) => resolveQuoteSalespeople(q, users)[0]?.name || null },
    { key: 'amount', type: 'money', value: (q) => getTotalAmount(q, q.id) },
    { key: 'acknowledgments', type: 'text', value: (q) => humanizeEnum(q.signatureStatus || q.signature_status || '') || null },
    { key: 'status', type: 'text', value: (q) => humanizeEnum(resolveDisplayStatus(q.status, 1)) || null },
    { key: 'quoteDate', type: 'date', value: (q) => quoteDateValue(q) },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [users, getEnumLabel]);
  const { sortedRows: sortedQuotes, getSortProps } = useTableSort(filteredQuotes, quoteSortColumns, {
    onSortChange: () => setPage(1),
  });

  // Pagination (matches Customers/Jobs pattern)
  const totalPages = Math.ceil(filteredQuotes.length / pageSize);
  const paginatedQuotes = sortedQuotes.slice((page - 1) * pageSize, page * pageSize);
  // Reset page when filter changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { setPage(1); }, [activeChip, debouncedSearch]);

  // PR-64 §1.1 (R-77) — selection helpers.
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };
  const toggleSelectAll = () => {
    if (paginatedQuotes.length === 0) return;
    if (selectedIds.length === paginatedQuotes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedQuotes.map(q => q.id));
    }
  };
  // Bulk action handler. Actions: convert (Convert to Job) / export / archive.
  const handleBulkAction = async (action) => {
    setBulkError(null);
    setBulkSuccess(null);
    const ids = [...selectedIds];
    if (ids.length === 0) return;

    if (action === 'convert') {
      // PR-64 §1.1 (379_ §1): the bulk-bar action is disabled unless
      // exactly one quote is selected AND its pipelineStatus is 'active',
      // matching the row-menu gate at :629. The upstream gate means the
      // handler is only invoked in the eligible case; the defensive check
      // below prevents a stale click after the selection changed.
      //
      // IDI-313 CONTROL 2 (2026-09-14): the defensive check MUST use the same
      // convertible set as the enabling gate below (CONVERTIBLE_STATES =
      // ['active','sent','won']). It previously required `=== 'active'`, so a
      // single selected `sent`/`won` quote enabled the button but the handler
      // silently returned — the exact clickable-and-silent defect CONTROL 2
      // forbids. (draft is already excluded from the gate, so it stays
      // correctly disabled.)
      if (ids.length !== 1) return;
      const q = quotes.find(x => x.id === ids[0]);
      const CONVERTIBLE = ['active', 'sent', 'won'];
      if (!q || !CONVERTIBLE.includes(normalizeStatusStatic(q.status))) return;
      navigate(`/quotes/${ids[0]}/record-decision`);
      return;
    }

    if (action === 'export') {
      const selected = quotes.filter(q => ids.includes(q.id));
      const rows = selected.map(q => ({
        quote_number: formatQuoteNumber(q),
        customer: q.customer_name || q.customerName || '',
        salespeople: normaliseSalespeople(q.salespeople || q).map(s => s.name || s).join(' | '),
        status: resolveDisplayStatus(q) || '',
        // IDI-215: was `q.total_cents ?? q.total` -- a DIFFERENT, non-
        // canonical precedence chain than the visible list (which uses
        // getTotalAmount: total_amount -> totalAmount -> total). A quote
        // could show one total on screen and a DIFFERENT one in its own
        // CSV export for the exact same record. getTotalAmount already
        // returns cents (fed directly into formatCurrency at line ~759
        // with no conversion), so divide by 100 here to match this row's
        // dollars unit.
        total: getTotalAmount(q, q.id) != null ? getTotalAmount(q, q.id) / 100 : 0,
        created_at: q.createdAt || q.created_at || '',
      }));
      const headers = Object.keys(rows[0] || { quote_number: '' });
      const escape = (v) => {
        const s = v == null ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [
        headers.join(','),
        ...rows.map(r => headers.map(h => escape(r[h])).join(',')),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quotes-${dealerToday()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setBulkSuccess(`Exported ${rows.length} quote${rows.length === 1 ? '' : 's'} to CSV.`);
      setSelectedIds([]);
      return;
    }

    if (action === 'archive') {
      const results = await Promise.allSettled(
        ids.map(id => quoteAPI.archiveQuote(id))
      );
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        // R-80 (2026-08-20): drafts are now archivable server-side. Any failures
        // here are genuine server errors, not the old "drafts must be deleted"
        // routing. Keep the message user-actionable without inventing a cause.
        setBulkError(
          `Archived ${ids.length - failures.length} of ${ids.length}. ${failures.length} could not be archived.`
        );
      } else {
        setBulkSuccess(`Archived ${ids.length} quote${ids.length === 1 ? '' : 's'}.`);
      }
      setSelectedIds([]);
      await loadQuotes();
    }
  };

  // PT-14 D1 (Wave 1, 2026-07-24): centralized routing helper so row-click
  // and kebab "View" stay in sync. Rev-3 rules (2026-07-29 §2 correction):
  //   draft/not_sent, active                      -> /quotes/:id/edit (builder)
  //   sent/viewed/pending/lost/archived/expired/declined -> /quotes/:id (locked detail)
  //   won/accepted                                -> /jobs/:jobId (fallback to detail)
  const getQuoteTargetRoute = getQuoteRoute;

  const formatDate = (d) => { if (!d) return '—'; try { const date = new Date(d); return isNaN(date.getTime()) ? '—' : `${date.getMonth()+1}/${date.getDate()}/${date.getFullYear()}`; } catch { return '—'; } };

  return (
    <>
    <div className="px-6">
      {/* R17-03: H1 "Quotes" left + "+ New Quote" red button right */}
      <PageHeader
        title="Quotes"
        actions={
          <Button onClick={() => navigate('/quotes/add')}>
            <PlusIcon className="h-4 w-4 mr-1" />
            New Quote
          </Button>
        }
      />

      {/* R17-03: Search left + chips right, one line, NO Filters button */}
      <ListShell
        search={searchTerm}
        onSearchChange={setSearchTerm}
        placeholder="Search by quote #, customer, or title..."
        chips={QUICK_FILTER_CHIPS.map(chip => {
          // PT-53A W2.2 (DEF-037 + DEF-117): chip count uses the SAME
          // `filterQuotesForChip` function that produces rendered rows.
          // One function, used twice. Invariant: count === rows.length —
          // holds once loading has settled (backgroundLoading is false).
          //
          // IDI-56: while a later batch is still streaming in, prefer the
          // server-computed aggregate (serverCounts) for this chip's total,
          // so counts don't visibly climb/flicker as `quotes` grows. Falls
          // back to the client-derived count immediately if the counts
          // fetch failed or hasn't resolved yet.
          const isArchivedChip = chip.key === 'archived';
          const clientCount = filterQuotesForChip(
            Array.isArray(quotes) ? quotes : [],
            chip.key,
            isArchivedChip,
          ).length;
          const chipCount =
            backgroundLoading && serverCounts && typeof serverCounts[chip.key] === 'number'
              ? serverCounts[chip.key]
              : clientCount;
          return {
            key: chip.key,
            label: chip.label,
            active: activeChip === chip.key,
            count: chipCount,
            onClick: () => setActiveChip(activeChip === chip.key ? null : chip.key),
          };
        })}
      >

      {error && <Alert variant="error" className="mb-4" onClose={() => setError(null)}>{error}</Alert>}
      {bulkError && <Alert variant="error" className="mb-4" onClose={() => setBulkError(null)}>{bulkError}</Alert>}
      {bulkSuccess && <Alert variant="success" className="mb-4" onClose={() => setBulkSuccess(null)}>{bulkSuccess}</Alert>}

      {/* PR-64 §1.1 (R-77): floating bulk-action bar. Returns null when count=0.
          `Convert to Job` is gated per 379_ §1:
            • exactly one quote selected (no batch convert API — each conversion
              needs modal-collected selections)
            • that quote's normalizeStatusStatic(status) === 'active', matching
              the row-menu gate at :629 (do NOT hand over a route the row menu
              deliberately withholds)
          BulkActionBar supports `disabled` + `title` per its :78 render loop;
          use those to surface WHY the action is unavailable, not an error
          after the click. */}
      {(() => {
        const singleSelected = selectedIds.length === 1
          ? quotes.find(q => q.id === selectedIds[0])
          : null;
        // IDI-133 (2026-09-01): convertible states are active, sent, won —
        // per operator ruling. Signature-required behaviour retired.
        // Expired quotes reactivate first (Reactivate row action).
        //
        // IDI-313 (2026-09-10): 'draft' REMOVED from this list. The prior
        // comment's premise ("Backend-draft is UI-active") was falsified by
        // IDI-313's finding: a genuinely-draft quote displays as Active in
        // the UI while the backend rejects convert_to_job with a 400
        // ("Cannot convert a quote in 'draft' status"). The real fix is
        // IDI-313's activation-on-first-save (assembleQuotePayload flips
        // draft→active once the quote has ≥1 line item), so by the time a
        // quote HAS anything worth converting, it's already 'active' and
        // matches the entry already in this list. A quote that's STILL
        // draft has zero line items — nothing to convert — so leaving it
        // out of CONVERTIBLE_STATES is now correct, not a gap.
        const CONVERTIBLE_STATES = ['active', 'sent', 'won'];
        const singleConvertible = singleSelected
          && CONVERTIBLE_STATES.includes(normalizeStatusStatic(singleSelected.status));
        const convertDisabledReason = selectedIds.length > 1
          ? 'Select a single convertible quote to convert.'
          : (singleSelected && !singleConvertible
              ? 'Only active, sent, or won quotes can be converted to a job. Expired quotes reactivate first.'
              : null);
        return (
          <BulkActionBar
            count={selectedIds.length}
            onClear={() => setSelectedIds([])}
            actions={[
              {
                label: 'Convert to Job',
                onClick: () => handleBulkAction('convert'),
                disabled: !singleConvertible,
                title: convertDisabledReason || undefined,
              },
              { label: 'Export',  onClick: () => handleBulkAction('export') },
              { label: 'Archive', onClick: () => handleBulkAction('archive') },
            ]}
          />
        );
      })()}

      {/* RF-17-1: NO KPI/summary cards — straight to table */}
      {/* IDI-56: informational only — more quote batches are still loading
          in the background behind the rows already shown below. */}
      {backgroundLoading && (
        <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>
          Loading more quotes…
        </p>
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader className="w-10">
              <Checkbox
                checked={selectedIds.length === paginatedQuotes.length && paginatedQuotes.length > 0}
                onChange={toggleSelectAll}
                aria-label="Select all quotes"
              />
            </TableHeader>
            <TableHeader {...getSortProps('project')}>Project</TableHeader>
            <TableHeader {...getSortProps('customer')}>Customer</TableHeader>
            {/* IDI-164: Job Type after Customer (Adrian 10-20). */}
            <TableHeader {...getSortProps('jobType')}>Job Type</TableHeader>
            <TableHeader {...getSortProps('salesperson')}>Salesperson</TableHeader>
            <TableHeader align="right" {...getSortProps('amount')}>Amount</TableHeader>
            {/* IDI-164 / D-10 (10-13): "Acknowledgments"; pill values unchanged. */}
            <TableHeader {...getSortProps('acknowledgments')}>Acknowledgments</TableHeader>
            <TableHeader {...getSortProps('status')}>Status</TableHeader>
            {/* IDI-164: Quote Date = date, else createdAt (the PDF rule). */}
            <TableHeader {...getSortProps('quoteDate')}>Quote Date</TableHeader>
            <TableHeader aria-label="Actions"></TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <>
              <SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow />
            </>
          ) : filteredQuotes.length === 0 ? (
            <TableRow>
              {/* IDI-273 / IDI-164: 10 header columns (checkbox, Project,
                  Customer, Job Type, Salesperson, Amount, Acknowledgments,
                  Status, Quote Date, Actions). SkeletonRow renders Array(10)
                  to match. */}
              <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                <EmptyState
                  mode={quotes.length === 0 ? 'first-run' : 'filtered'}
                  title={quotes.length === 0 ? 'No quotes yet.' : 'No quotes match your filters.'}
                  action={
                    <Button variant="primary" size="sm" onClick={() => navigate('/quotes/add')}>Create Your First Quote</Button>
                  }
                  onClearFilters={() => { setSearchTerm(''); setActiveChip('active'); }}
                />
              </TableCell>
            </TableRow>
          ) : (
            paginatedQuotes.map((quote) => {
              const pipelineStatus = normalizeStatusStatic(quote.status);
              const sigStatus = quote.signatureStatus || quote.signature_status || '';
              return (
                <TableRow
                  key={quote.id}
                  onClick={() => navigate(getQuoteTargetRoute(quote), { state: QUOTES_LIST_ORIGIN })}
                  className={selectedIds.includes(quote.id) ? 'bg-[color:var(--color-bg-selected)]' : undefined}
                >
                  {/* PR-64 §1.1: per-row select checkbox. stopPropagation so
                      the click doesn't navigate to the quote. */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.includes(quote.id)}
                      onChange={() => toggleSelect(quote.id)}
                      stopPropagation
                      aria-label={`Select ${quote.title || formatQuoteNumber(quote)}`}
                    />
                  </TableCell>
                  <TableCell className="font-semibold" href={getQuoteTargetRoute(quote)} hrefState={QUOTES_LIST_ORIGIN}>
                    {quote.title || quote.quoteTitle || quote.quote_title || quote.projectName || quote.project_name || formatQuoteNumber(quote)}
                    <span className="block text-xs font-normal" style={{ color: "var(--color-text-muted)" }}>{formatQuoteNumber(quote)}</span>
                  </TableCell>
                  <TableCell>{quote.customerName || quote.customer_name || "—"}</TableCell>
                  <TableCell>{jobTypeLabelFor(quote)}</TableCell>
                  <TableCell className="text-xs text-gray-500">
                    {/* PT-53b F8b (2026-08-03): peer set via normaliseSalespeople + local
                        id→name resolve against the users cache. Renders via <SalespeopleChips>
                        (canonical treatment; `lvalley · Adrian · +2`). Peers, no designated
                        member — order = as-assigned (U-17 exemption at APP-02:531). */}
                    {(() => {
                      // IDI-164: first named salespersons[] (id-only entries
                      // resolved via the users cache) → salespersonName (server
                      // resolved from assignedTo) → '—'. Never a raw id.
                      const resolved = resolveQuoteSalespeople(quote, users);
                      if (resolved.length === 0) return '—';
                      return <SalespeopleChips salespeople={resolved} interactive={false} />;
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    {getTotalAmount(quote, quote.id) != null
                      ? formatCurrency(getTotalAmount(quote, quote.id))
                      : '\u2014'}
                  </TableCell>
                  <TableCell>
                    {sigStatus ? (
                      <SignaturePill signatureStatus={sigStatus} size="sm" />
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </TableCell>
                  <TableCell><StatusPill status={resolveDisplayStatus(quote.status, 1)} /></TableCell>
                  <TableCell>{formatDate(quoteDateValue(quote))}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    {/* PT-39a: hand-rolled Menu block retired for the DropdownMenu
                        atom. Row-context menu — stopPropagation prevents the row
                        click navigating to the quote target when the user is
                        aiming for a menu item. */}
                    <DropdownMenu
                      ariaLabel={`Actions for ${formatQuoteNumber(quote)}`}
                      stopPropagation
                      items={[
                        // PT-14 D1c (Wave 1): kebab "View" mirrors row-click routing (status-driven)
                        { label: 'View', onClick: () => navigate(getQuoteTargetRoute(quote), { state: QUOTES_LIST_ORIGIN }) },
                        // §3 (2026-07-29) D-10: Send / Resend + Download PDF. Raw status distinguishes
                        //   "sent" from other "active" cases (won/accepted). D-10 mandates both actions.
                        (quote.status === 'draft' || quote.status === 'not_sent') && { label: 'Send', onClick: () => navigate(`/quotes/${quote.id}/review`) },
                        // IDI-549: a signed quote can't be sent again (server 409s), so no Resend.
                        (quote.status === 'sent' || quote.status === 'viewed' || quote.status === 'pending') && sigStatus !== 'acknowledged' && { label: 'Resend', onClick: () => navigate(`/quotes/${quote.id}/review`) },
                        // D-10 Download PDF (all statuses except lost/archived). Uses existing quoteAPI.generatePDF.
                        quote.status !== 'lost' && !(quote.archivedAt || quote.archived_at) && { label: 'Download PDF', onClick: () => handleDownloadPDF(quote.id) },
                        // IDI-544: active only — a signed (won/accepted) quote is never Mark as Lost.
                        pipelineStatus === 'active' && { label: 'Mark as Lost', variant: 'danger', onClick: () => handleMarkAsLost(quote.id) },
                        // IDI-313 (2026-09-10): Convert available on active, sent, or won only.
                        // 'draft' removed — a draft quote has zero line items (nothing to
                        // convert); once it has line items, it activates on first save.
                        (pipelineStatus === 'active' || pipelineStatus === 'sent' || pipelineStatus === 'won') && { label: 'Convert to Job', onClick: () => navigate(`/quotes/${quote.id}/record-decision`) },
                        // Edit removed (operator ruling 08-09): byte-identical to View for drafts.
                        // PT-14 G4 (Wave 3, 2026-07-24): reactivate for expired quotes. Mirrors QuoteDetail.js handleReactivate.
                        pipelineStatus === 'expired' && { label: 'Reactivate', onClick: () => handleReactivateQuote(quote.id) },
                        // PT-14 D2 · R-80 (2026-08-20): Archive is reversible; neutral styling.
                        // Available on ALL statuses INCLUDING draft. The backend's prior refusal
                        // to archive drafts (a legacy implementation opinion, per operator ruling
                        // 08-09 §3e-v) was removed at backend-go/cmd/quotes/main.go archiveQuote handler. With
                        // Archive accepting drafts, R-80 ("we never delete") is enforceable, and
                        // the drafts-only Delete entry that used to sit here — kept alive by
                        // that backend refusal — was removed in PR-65 (386_ §2 measured the
                        // guard is gone). See PR-65 Nevado report; frontend deleteQuote flagged
                        // as Family B (endpoint orphaned after this commit).
                        !(quote.archivedAt || quote.archived_at) && { label: 'Archive', onClick: () => handleArchive(quote.id) },
                        // PT-53A W2.2 (DEF-036): Unarchive row action for archived quotes.
                        !!(quote.archivedAt || quote.archived_at) && { label: 'Unarchive', onClick: () => handleUnarchive(quote.id) },
                      ].filter(Boolean)}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-4 pb-4">
          <p className="text-sm text-gray-500">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filteredQuotes.length)} of {filteredQuotes.length}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
            <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
            <Button variant="secondary" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
          </div>
        </div>
      )}
      </ListShell>
    </div>
    {/* §1.1 (2026-07-29): Mark-as-Lost modal renders once at the page level,
        controlled by markLostQuoteId. Captures loss reason + "Also archive". */}
    <MarkAsLostModal
      open={markLostQuoteId != null}
      onCancel={closeMarkLost}
      onSubmit={submitMarkLost}
    />
    </>
  );
}
